<template>
  <UserNavbar />
  <div class="container">
    <div>
      <h1>검사 결과 다운로드</h1>
    </div>
    <div class="form-section">
      <div class="form-group">
        <label for="name">검사종류</label>
        <span>{{ projectName }}</span>
      </div>
      <div class="form-group">
        <label for="name">이름</label>
        <input
          type="text"
          id="name"
          v-model="formModel.name"
          placeholder="이름을 입력하세요"
        />
      </div>
      <div class="form-group">
        <label for="birthday">생년월일 (8자리) (예: 19990101)</label>
        <input
          type="text"
          id="birthday"
          v-model="formModel.birthday"
          placeholder="YYYYMMDD"
          maxlength="8"
        />
      </div>
      <n-button
        :loading="isDownloading"
        type="primary"
        @click="submitDownload()"
      >
        다운로드
      </n-button>
    </div>
  </div>
  <UserFooter />
</template>
<script setup lang="ts">
import UserNavbar from "@/components/UserNavbar.vue";
import UserFooter from "@/components/UserFooter.vue";
import { ref, onMounted } from "vue";
import * as common from "@/assets/common/common";
import { getRequestEndpoint } from "@/config/env";
import { useRoute } from "vue-router";
import { useNotification } from "naive-ui";
const route = useRoute();
const projectSKFromRoute = ref<string | null>(null);
const projectTypeFromRoute = ref<string | null>(null);
const projectName = ref<string | null>(null);
const pageLoading = ref(false);
const isDownloading = ref(false);

const notification = useNotification();

interface UserInfo {
  name: string;
  birthday: string;
}
const formModel = ref<UserInfo>({
  name: "",
  birthday: "",
});

async function submitDownload() {
  if (!formModel.value.name) {
    notification.error({
      title: "입력 오류",
      description: "이름을 입력해주세요.",
      duration: 3000,
    });
    return;
  }
  if (!formModel.value.birthday || !/^\d{8}$/.test(formModel.value.birthday)) {
    notification.error({
      title: "입력 오류",
      description: "생년월일 8자리(YYYYMMDD)를 정확히 입력해주세요.",
      duration: 3000,
    });
    return;
  }
  isDownloading.value = true;
  await downloadReportFile();
  isDownloading.value = false;
}

async function downloadReportFile() {
  if (!projectSKFromRoute.value) {
    notification.error({
      title: "오류",
      description: "다운로드할 파일 정보가 없습니다.",
      duration: 3000,
    });
    return;
  }

  pageLoading.value = true;

  try {
    const downloadFunctionUrl = getRequestEndpoint("examineeDownload");
    if (!downloadFunctionUrl || typeof downloadFunctionUrl !== "string") {
      throw new Error("다운로드 서비스 URL을 가져올 수 없습니다.");
    }

    const response = await fetch(downloadFunctionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        SK: projectSKFromRoute.value,
        name: formModel.value.name,
        birthday: formModel.value.birthday,
      }),
    });

    if (!response.ok) {
      let errorBodyText = `HTTP 오류: ${response.status}`;
      try {
        const errorResult = await response.json();
        errorBodyText =
          common.getResultError(errorResult) ||
          errorResult.message ||
          errorResult.error ||
          `서버 오류: ${response.status}`;
      } catch (e) {
        // 응답이 JSON이 아니거나 파싱 실패 시 statusText 사용
        errorBodyText = response.statusText || `서버 오류: ${response.status}`;
      }
      throw new Error(errorBodyText);
    }

    const blob = await response.blob();
    let filename = formModel.value.name;
    if (projectTypeFromRoute.value) {
      const typePrefix = projectTypeFromRoute.value.split("_")[0].toUpperCase();
      filename = `${typePrefix}-${filename}`;
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    notification.success({
      title: "성공",
      description: `${filename} 파일 다운로드가 시작되었습니다.`,
      duration: 3000,
    });
  } catch (error: any) {
    console.error("파일 다운로드 실패:", error);
    notification.error({
      title: "다운로드 실패",
      description: error.message || "파일 다운로드 중 오류가 발생했습니다.",
      duration: 5000,
    });
  } finally {
    pageLoading.value = false;
  }
}

onMounted(async () => {
  projectSKFromRoute.value = route.params.SK as string;
  projectTypeFromRoute.value = route.params.TYPE as string;
  projectSKFromRoute.value = decodeURIComponent(projectSKFromRoute.value);
  projectTypeFromRoute.value = decodeURIComponent(projectTypeFromRoute.value);
  projectName.value = common.getProjectName(projectTypeFromRoute.value);
});

// If you need to use the notification object from a library like Naive UI,
// and it's not globally registered, you'd typically get it from a composable:
//
// import { useMessage, useNotification } from 'naive-ui' // Example for Naive UI
//
// // Inside setup():
// const message = useMessage()
// const notification = useNotification()
//
// // Then use `notification.error({...})` or `message.error(...)`
</script>
<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}
.form-section {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 300px;
}
.form-group {
  display: flex;
  flex-direction: column;
}
.form-group label {
  margin-bottom: 5px;
  font-weight: bold;
}
.form-group input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.download-button {
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}
.download-button:hover {
  background-color: #0056b3;
}
</style>
